/* eslint-disable no-console */
/* eslint-disable max-len */
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { toggleImageFavorite } from '../../../actions/image_management';
import { toggleVideoFavorite } from '../../../actions/video_management';
import { toggleDocumentFavorite } from '../../../actions/document_management';
import { toggleArticleFavorite } from '../../../actions/article_management';
import { togglePodcastFavorite } from '../../../actions/podcast_management';
import { togglePlaylistFavorite } from '../../../actions/playlist_management/playlists';
import { useSelector } from 'react-redux';
type ContentType = 'image' | 'document' | 'article' | 'video' | 'podcast' | 'playlist' | 'images' | 'videos' | 'documents' | 'articles' | 'podcasts' | 'playlists';

interface FavoriteResponse {
	success: boolean;
	data: {
		isFavorited: boolean;
		favoriteCount: number;
		[key: string]: any;
	};
	message: string;
}

interface UseFavoriteProps {
	contentType: ContentType;
	contentId: string;
	initialFavoriteState?: boolean;
}

const toggleFavoriteByType = async (
	contentType: ContentType,
	contentId: string,
	companyId?: string
): Promise<FavoriteResponse> =>
{
	switch (contentType)
	{
		case 'image':
		case 'images':
			if (!companyId) throw new Error('Company ID is required for image favorites');

			return toggleImageFavorite(companyId, contentId);
		case 'video':
		case 'videos':
			if (!companyId) throw new Error('Company ID is required for video favorites');

			return toggleVideoFavorite(companyId, contentId);
		case 'document':
		case 'documents':
			if (!companyId) throw new Error('Company ID is required for document favorites');

			return toggleDocumentFavorite(companyId, contentId);
		case 'article':
		case 'articles':
			if (!companyId) throw new Error('Company ID is required for article favorites');

			return toggleArticleFavorite(companyId, contentId);
		case 'podcast':
		case 'podcasts':
			return togglePodcastFavorite(contentId);
		case 'playlist':
		case 'playlists':
			if (!companyId) throw new Error('Company ID is required for playlist favorites');

			return togglePlaylistFavorite(contentId, companyId);
		default:
			throw new Error(`Unsupported content type: ${contentType}`);
	}
};

export const useFavorite = ({
	contentType,
	contentId,
	initialFavoriteState = false
}: UseFavoriteProps) =>
{
	const [ isFavorite, setIsFavorite ] = useState<boolean>(initialFavoriteState);
	const [ isLoading, setIsLoading ] = useState<boolean>(false);
	const [ favoriteCount, setFavoriteCount ] = useState<number>(0);
	const userData = useSelector((state: any) => state?.mysession?.session);

	// Add useEffect to handle initialFavoriteState changes
	useEffect(() => {
		setIsFavorite(initialFavoriteState);
	}, [ initialFavoriteState ]);

	const toggleFavorite = async () =>
	{
		setIsLoading(true);
		setIsFavorite((prev) => !prev);

		try
		{
			const result = await toggleFavoriteByType(contentType, contentId, userData?.companyId);

			if (result.success)
			{
				// No need to update isFavorite here since we already did it optimistically
				setFavoriteCount(result.data.favoriteCount);
				toast.success(result.message);
			}
			else
			{
				// If API fails, revert the optimistic update
				setIsFavorite((prev) => !prev);
				throw new Error(result.message);
			}
		}
		catch (error)
		{
			console.error('Error toggling favorite:', error);
			toast.error(error instanceof Error ? error.message : 'Failed to update favorite status');
			// Revert optimistic update if needed
			setIsFavorite((prev) => !prev);
		}
		finally
		{
			setIsLoading(false);
		}
	};

	return {
		isFavorite,
		isLoading,
		favoriteCount,
		toggleFavorite
	};
};